import { Component } from "@angular/core";
import { CollectionService } from "../../collections/collection.service";
import { NgbModal, NgbTooltipModule } from "@ng-bootstrap/ng-bootstrap";
import { LayoutService } from "../../../shared/services/layout.service";
import { FormsModule } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TableComponent } from "../../../shared/components/table/table.component";
import { ActionsTable, HeaderTables, SortType } from "../../../shared/models";
import { FORMAT_XSLX } from "../../../shared/constants/constants";
import { BreadcrumbService } from "../../../shared/services/breadcrumb.service";
import { EditCouponUserInfoComponent } from "../../modal/edit-coupon-user-info/edit-coupon-user-info";
import { EventsService } from "../../events/event.service";
import { HttpClient } from "@angular/common/http";
import { formatDate } from "@angular/common";
import { registerLocaleData } from "@angular/common";
import localeIt from "@angular/common/locales/it";
import localeRu from "@angular/common/locales/ru";

registerLocaleData(localeIt);
registerLocaleData(localeRu);
@Component({
  selector: "app-collections-list",
  standalone: true,
  imports: [NgbTooltipModule, FormsModule, TranslateModule, TableComponent],
  templateUrl: "./collections-list.component.html",
  styleUrl: "./collections-list.component.scss",
})
export class CollectionsListComponent {
  public collections: any[] = []; // Dati della tabella
  totalItems = 0; // Numero totale di elementi
  currentPage = 1; // Pagina corrente
  itemsPerPage = 15; // Elementi per pagina
  totalPages: number = 1;
  isLoading: boolean = true;
  headers: HeaderTables[] = [];
  columnWidth: Array<any> = [];
  actions: ActionsTable[] = [];
  token: string;
  genericErrorTranslated = "";
  currentLocale = this.translateService.currentLang;

  filtersParams: {
    collectionId: string | null;
    type: string | null;
    email_like: string | null;
    name_like: string | null;
    surname_like: string | null;
    profession_like: string | null;
    coupon_like: string | null;
    status_like: string | null;
  } = {
    collectionId: null,
    type: null,
    email_like: null,
    name_like: null,
    surname_like: null,
    profession_like: null,
    coupon_like: null,
    status_like: null,
  };

  sortingParams: SortType = undefined;

  translationEntity = {
    "button_labels.confirm": "",
    "button_labels.cancel": "",
    "search-coupon-modal.coupon_code": "",
    "forms.status": "",
    "forms.datetime_request": "",
    "statuses.redeemed_at": "",
    "forms.store": "",
    "forms.qta_prduct": "",
    warning: "",
    "users.modal_reset_password": "",
    "users.modal_delete_user": "",
  };

  constructor(
    public collectionService: CollectionService,
    private layoutService: LayoutService,
    private translateService: TranslateService,
    private activeModal: NgbModal,
    private eventService: EventsService,
    private breadcrumbService: BreadcrumbService,
    public http: HttpClient,
  ) {
    this.translateService
      .get(Object.keys(this.translationEntity))
      .subscribe((res) => {
        this.translationEntity = res;
        this.headers = [
          { label: "#" },
          {
            label: this.translationEntity["search-coupon-modal.coupon_code"],
            key: "code",
          },
          { label: this.translationEntity["forms.status"], key: "status" },
          {
            label: this.translationEntity["forms.datetime_request"],
            key: "datetime_request",
          },
          {
            label: this.translationEntity["statuses.redeemed_at"],
            key: "datetime_redeem",
          },
          { label: this.translationEntity["forms.store"], key: "store" },
          { label: this.translationEntity["forms.qta_prduct"] },
        ];
      });
    this.columnWidth = ["5%", "20%", "20%", "15%", "20%", "20%", "10%"];

    this.genericErrorTranslated = this.translateService.instant(
      "errors.generic_error",
    );
  }

  ngOnInit(): void {
    this.translateService
      .get(["routes.report", "routes.current_collections"])
      .subscribe((translations) => {
        this.breadcrumbService.changeBreadCrumbProps({
          title: translations["routes.current_collections"],
          preTitle: translations["routes.report"],
          eventDetailButtons: false,
          token: "token",
        });
      });

    this.loadData();

    this.layoutService.loading$.subscribe((loading) => {
      this.isLoading = loading;
    });

    this.eventService.event$.subscribe((eventData) => {
      if (eventData === "refreshCustomer") {
        this.loadData();
      }
    });
  }

  onFilterChange(searchFilter: any): void {
    this.currentPage = 1; // Ritorna alla prima pagina per i risultati del filtro

    // Costruisce la chiave del filtro API (es. 'email' -> 'email_like')
    const filterKey =
      `${searchFilter.key}_like` as keyof typeof this.filtersParams;

    // Se la chiave del filtro è valida, aggiorna il valore
    if (this.filtersParams.hasOwnProperty(filterKey)) {
      // Imposta il valore, o null se la stringa è vuota (per non inviare un parametro vuoto)
      this.filtersParams[filterKey] = searchFilter.value || null;
    }

    // Ricarica i dati con i nuovi filtri
    this.loadData();
  }

  loadData() {
    const paginationParams = {
      page: this.currentPage,
      per_page: this.itemsPerPage,
    };

    // sorting params
    let sortParams = this.sortingParams
      ? {
          _sort: this.sortingParams._sort,
          _order: this.sortingParams._order,
        }
      : {};

    if (sortParams._sort == "statusTranslated") {
      sortParams._sort = "status";
    }

    const allParams = {
      ...paginationParams,
      ...this.filtersParams,
      ...sortParams,
    };

    this.collectionService
      .getCollectionsReport(allParams)
      .subscribe((response: any) => {
        if (response.success) {
          this.collections = response.data.data.map(
            (
              element: {
                code: string;
                status: string;
                source: any;
                datetime_request: string;
                datetime_redeem: string;
                store: any;
                products: Array<{
                  code: string;
                  name: string;
                  quantity: string;
                  uom: string;
                }>;
              },
              index: number,
            ) => {
              // Somma delle quantità dei prodotti
              const totalQuantity =
                element && element.products ? element.products.length : 0;
              const formattedQuantity = totalQuantity
                .toFixed(2)
                .replace(".", ",");

              // Formatta la data in base alla lingua corrente
              const formattedDate = element.datetime_redeem
                ? formatDate(
                    element.datetime_redeem,
                    "fullDate",
                    this.currentLocale,
                  )
                : "";

              const formattedDateRequest = element.datetime_request
                ? formatDate(
                    element.datetime_request,
                    "fullDate",
                    this.currentLocale,
                  )
                : "";

              return {
                code: element.code,
                idEnumerated:
                  (this.currentPage - 1) * this.itemsPerPage + index + 1,
                status: element.status ?? "",
                source: element.source?.name ?? "",
                datetime_request: formattedDateRequest,
                datetime_redeem: formattedDate,
                store: element.store?.name ?? "",
                products: formattedQuantity,
              };
            },
          );

          this.currentPage = response.data.current_page;
          this.totalPages = response.data.last_page;
          this.totalItems = response.data.total;
          this.itemsPerPage = response.data.per_page;
        }
      });
  }

  onPageChange(newPage: number) {
    this.currentPage = newPage;
    this.loadData(); // Ricarica i dati con la nuova pagina
  }

  onItemsPerPageChange(newPaginationSize: number) {
    this.currentPage = 1;
    this.itemsPerPage = newPaginationSize;
    this.loadData();
  }

  openEditCollectionModal(user: any) {
    const modal = this.activeModal.open(EditCouponUserInfoComponent, {
      centered: true,
      backdrop: "static",
      size: "lg",
    });
    modal.componentInstance.user = user;
    modal.componentInstance.modalType = "edit";
    modal.result
      .then((result) => {
        this.loadData();
      })
      .catch((error) => {});
  }

  onSortChanged(sortParams: SortType) {
    this.sortingParams = sortParams;
    this.loadData();
  }

  exportReport() {
    this.isLoading = true;
    const format = FORMAT_XSLX;
    this.collectionService.getExportReport(this.token, format).subscribe({
      next: (response: any) => {
        const blob = response;
        const downloadUrl = window.URL.createObjectURL(blob);

        // Crea un link temporaneo
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `export_report_collection.${format}`;
        link.click();

        window.URL.revokeObjectURL(downloadUrl);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }
}
